import { useQuery, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { GetSinglePrivilegeResponse } from '@upright/common/iam/privilege/api-types';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { Privilege, PrivilegeType } from '@upright/common/iam/privilege/types';
import { createPrivilege, deletePrivilege, getMyResourcelessPrivilege, getSinglePrivilege } from '.';
import { ErrorWithHttpStatus } from '../../common/error-with-http-status';
import { queryClient } from '../../common/react-query';
import { useGetLoggedInUser, useIsTeam, useViewSetting } from '@upright/ui/common/generic/hooks';

/**
 * Query options for "does this exact privilege exist". The endpoint answers with the privilege
 * row when it is granted and `false` when it is not, so `data` is truthy exactly when the
 * caller holds it — and because the query is keyed on the full privilege object, any row that
 * comes back is the one that was asked about. Read it as `!!data`; do not compare
 * `data.permissionId` against the id you asked for, which reads as a check but cannot fail.
 */
export function getSinglePrivilegeQueryOptions<T extends PrivilegeType>(
  privilege: Privilege<T>,
): UseQueryOptions<GetSinglePrivilegeResponse<T>, ErrorWithHttpStatus> {
  return {
    queryKey: ['privilege', privilege],
    queryFn: async () => await getSinglePrivilege(privilege),
  };
}

export function getCreatePrivilegeMutationOptions(): UseMutationOptions<void, ErrorWithHttpStatus, Privilege> {
  return {
    mutationFn: async (privilege: Privilege) => {
      await createPrivilege(privilege);
    },
    onSuccess: (_, privilege) => {
      void queryClient.invalidateQueries({ queryKey: ['privilege', privilege] });
      // Toggling a privilege from the team-internal admin views can also
      // affect what `useHasResourcelessPrivilege` returns for users in that
      // org. We can't tell from here whether the toggled privilege is for
      // the calling user, so invalidate the `myResourcelessPrivilege`
      // family broadly — it's only ever read by the same-tab user, so
      // over-invalidation is harmless.
      void queryClient.invalidateQueries({ queryKey: ['myResourcelessPrivilege'] });
    },
  };
}

export function getDeletePrivilegeMutationOptions(): UseMutationOptions<void, ErrorWithHttpStatus, Privilege> {
  return {
    mutationFn: async (privilege: Privilege) => {
      await deletePrivilege(privilege);
    },
    onSuccess: (_, privilege) => {
      void queryClient.invalidateQueries({ queryKey: ['privilege', privilege] });
      // See note on the create mutation above — keeps
      // `useHasResourcelessPrivilege` consumers (e.g. an open `/chat` tab)
      // in sync when access is revoked.
      void queryClient.invalidateQueries({ queryKey: ['myResourcelessPrivilege'] });
    },
  };
}

/**
 * Returns whether the current user holds the given resourceless privilege.
 * Team users short-circuit to `true` (the backend's
 * `assertUserHasResourcelessPrivilege` does the same), so frontend gates
 * render consistently with the backend's authorization decision and we save
 * a roundtrip for team accounts.
 *
 * Anonymous visitors short-circuit to `false` without firing the query —
 * `/api/me/resourceless-privilege/...` requires authentication, so the call
 * would only ever return 401. Skipping it keeps the public site quiet for
 * always-mounted callers like the top-bar `AskAiLink` entry point.
 *
 * `loading` is `true` while the privilege query is in flight so callers can
 * avoid flashing "no access" before the answer comes back.
 */
export function useHasResourcelessPrivilege(permissionId: ResourcelessPermissionId): {
  hasPrivilege: boolean;
  loading: boolean;
} {
  const isTeam = useIsTeam();
  const loggedInUser = useGetLoggedInUser();
  const isAnonymous = !loggedInUser;
  const { data, isPending } = useQuery({
    queryKey: ['myResourcelessPrivilege', permissionId],
    queryFn: async () => await getMyResourcelessPrivilege(permissionId),
    enabled: !isTeam && !isAnonymous,
  });
  if (isTeam) {
    return { hasPrivilege: true, loading: false };
  }
  if (isAnonymous) {
    return { hasPrivilege: false, loading: false };
  }
  return { hasPrivilege: data?.hasPrivilege ?? false, loading: isPending };
}

/**
 * Whether the current user should see the segmented "Products & services"
 * editor (List / Hierarchy toggle, segment hierarchy tree, and the explicit
 * Edit / Save / Recompute paradigm). The single gate every product-editor
 * surface reads (PLT-5846).
 *
 * Two audiences, resolved separately on purpose:
 * - Customers (non-team) are gated on the `USE_PRODUCT_SEGMENTS` resourceless
 *   privilege. It is off by default and switched on per user group by hand from
 *   the privileges admin UI during the managed rollout (intended pilot audience:
 *   DMA Consultant / DMA Freemium groups).
 * - Team users keep opting in via the internal `blanket.plgSegmentEditor` View
 *   setting (default off), so the work-in-progress editor is not forced onto
 *   every internal dashboard. We deliberately do NOT reuse the privilege's
 *   team-shortcut here (which would return `true` for every team user and take
 *   the toggle away).
 *
 * Returns a bare boolean: `false` while the privilege query is still in flight
 * for a customer, flipping to `true` once it resolves. Consumers that key their
 * initial edit/view mode off this value must tolerate that transition — see the
 * derived `isEditing` in `useProductInputDataState`.
 */
export function useHasProductSegmentsAccess(): boolean {
  const isTeam = useIsTeam();
  const teamViewSettingEnabled = useViewSetting('blanket.plgSegmentEditor');
  const { hasPrivilege } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_PRODUCT_SEGMENTS);
  return isTeam ? teamViewSettingEnabled : hasPrivilege;
}
